import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Grid, 
  TextField, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  MenuItem, 
  Select, 
  Typography, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Switch
} from '@mui/material';
import { 
  addMonths, 
  subMonths, 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isWeekend,
  startOfWeek,
  endOfWeek 
} from 'date-fns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';

import API_BASE_URL from '../config';

const CalendarSchedules = () => {
  const [scheduleName, setScheduleName] = useState('');
  const [speedLimit, setSpeedLimit] = useState(0);
  const [selectedSpeedLimit, setSelectedSpeedLimit] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [changeImage, setChangeImage] = useState(false);
  const [timeSlots, setTimeSlots] = useState(Array(4).fill({ start: null, end: null }));
  const [existingSchedule, setExistingSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [primaryColor, setPrimaryColor] = useState(localStorage.getItem('primaryColor') || '#007bff');
  const [secondaryColor, setSecondaryColor] = useState(localStorage.getItem('secondaryColor') || '#6c757d');
  const [textColor, setTextColor] = useState(localStorage.getItem('darkMode') === 'true' ? '#ffffff' : '#000000');
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImageType, setSelectedImageType] = useState('');
  const [selectedRegularImage, setSelectedRegularImage] = useState('');
  const [selectedSpeedingImage, setSelectedSpeedingImage] = useState('');
  const darkMode = localStorage.getItem('darkMode') === 'true';
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [selectedImage, setSelectedImage] = useState('');
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [imageDialogType, setImageDialogType] = useState('');
  const [userMediaImages, setUserMediaImages] = useState({});

  const [flashEnabled, setFlashEnabled] = useState(false);
  const [wigWagEnabled, setWigWagEnabled] = useState(false);


  const [packageAllowance, setPackageAllowance] = useState(false);


  const navigate = useNavigate();

  const monthsToDisplay = [
    subMonths(currentMonth, 1),
    currentMonth,
    addMonths(currentMonth, 1)
  ];

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });
  

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImage('');
  };
  
  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const isCurrentYear = (date) => date.getFullYear() === new Date().getFullYear();
  const getWorkdays = () => {
    const year = new Date().getFullYear();
    const days = [];
    let date = new Date(year, 0, 1);
    
    while(date.getFullYear() === year) {
      if(!isWeekend(date)) {
        days.push({ month: date.getMonth() + 1, day: date.getDate() });
      }
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  useEffect(() => {
    fetchPackageAllowance();
    fetchUserMediaList();
  }, []);

  const fetchPackageAllowance = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/package/calendar_schedule`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
  
        if (response.status === 401) {
          navigate('/unauthorized');
          return;
        }
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const data = await response.json();
  
        setPackageAllowance(data === 1);
  
      } catch (error) {
        console.error('Error fetching user package:', error);
        setError('Failed to fetch user package. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

  const fetchUserMediaList = useCallback(async () => {
      setIsLoadingImages(true);
      try {
        const response = await fetch(`${API_BASE_URL}/device/media-list`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const mediaFiles = await response.json();
        const imagePromises = mediaFiles.map(async (file) => {
          const imageResponse = await fetch(`${API_BASE_URL}/device/media-image/${encodeURIComponent(file.file_name)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (!imageResponse.ok) {
            console.error(`Failed to fetch image for ${file.file_name}`);
            return null;
          }
          const blob = await imageResponse.blob();
          return { name: file.file_name, url: URL.createObjectURL(blob) };
        });
  
        const images = await Promise.all(imagePromises);
        const imageMap = images.reduce((acc, img) => {
          if (img) {
            acc[img.name] = img.url;
          }
          return acc;
        }, {});
        setUserMediaImages(imageMap);
      } catch (error) {
        console.error('Error fetching user media images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    }, []);


  useEffect(() => {
    const checkLocalStorage = () => {
      setPrimaryColor(localStorage.getItem('primaryColor') || (darkMode ? '#0f0f0f' : '#f5f5f5'));
      setSecondaryColor(localStorage.getItem('secondaryColor') || (darkMode ? '#1a1a1a' : '#e0e0e0'));
      setTextColor(localStorage.getItem('darkMode') === 'true' ? '#ffffff' : '#000000');
    };
    
    const intervalId = setInterval(checkLocalStorage, 200);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchCalendarSchedule = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/device/calendar-schedule`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();

          console.log(data); // debug

          if (data) {
            setExistingSchedule(data);
            setScheduleName(data.calendar_schedule_name || '');
            setSelectedDates(data.dates || []);
            setTimeSlots([
              { start: data.start1 || null, end: data.end1 || null },
              { start: data.start2 || null, end: data.end2 || null },
              { start: data.start3 || null, end: data.end3 || null },
              { start: data.start4 || null, end: data.end4 || null }
            ]);
            setChangeImage(data.change_image === 1);
            setSelectedRegularImage({
              name: data.regular_content || '',
              blob: data.regular_content ? `${API_BASE_URL}/device/media-image/${data.regular_content}` : ''
            });
            setSelectedSpeedingImage({
              name: data.speeding_content || '',
              blob: data.speeding_content ? `${API_BASE_URL}/device/media-image/${data.speeding_content}` : ''
            });
            setSelectedSpeedLimit(data.change_speed === 1);
            setSpeedLimit(data.speed_limit || 0);
            setFlashEnabled(data.flash === 1);
            setWigWagEnabled(data.wigwag === 1);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching calendar schedule:', error);
        setError('Failed to load calendar schedule');
        setLoading(false);
      }
    };
    
    fetchCalendarSchedule();
  }, []);

  const handleDateSelect = (date) => {
    const newDate = { month: date.getMonth() + 1, day: date.getDate() };
    setSelectedDates(prev => 
      prev.some(d => d.month === newDate.month && d.day === newDate.day) 
        ? prev.filter(d => !(d.month === newDate.month && d.day === newDate.day))
        : [...prev, newDate]
    );
  };

  const handleTimeChange = (index, type, value) => {
    const newSlots = [...timeSlots];
    newSlots[index] = { ...newSlots[index], [type]: value === '' ? null : Number(value) };
    setTimeSlots(newSlots);
  };

  

  const validateTimeSlots = () => {
    // Pure validation function that returns error message
    const slots = timeSlots;
    
    // Check for partial slots
    for (let slot of slots) {
      if ((slot.start !== null && slot.end === null) || (slot.end !== null && slot.start === null)) {
        return 'Each time slot must have both start and end times';
      }
    }
  
    // Check ordering and overlaps
    let prevEnd = -1;
    for (let i = 0; i < slots.length; i++) {
      const { start, end } = slots[i];
      if (start === null || end === null) continue;
      
      if (start >= end) {
        return `Slot ${i+1}: End time must be after start time`;
      }
      if (start < prevEnd) {
        return `Slot ${i+1}: Must start after previous slot ends (${prevEnd}:00)`;
      }
      prevEnd = end;
    }
    
    return null;
  };

  const handleSave = async () => {
    const validationError = validateTimeSlots();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (selectedDates.length === 0) {
      setError('Please select at least one date');
      return;
    }

    if (selectedSpeedLimit && speedLimit <= 0) {
      setError('Speed limit must be greater than 0');
      return;
    }

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Brisbane';
      const response = await fetch(`${API_BASE_URL}/device/calendar-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          scheduleName,
          selectedDates,
          timeSlots,
          changeImage,
          selectedRegularImage,
          selectedSpeedingImage,
          selectedSpeedLimit,
          speedLimit,
          timezone,
          flashEnabled,
          wigWagEnabled
        })
      });

      if (!response.ok) throw new Error('Save failed');
        setError('');
      } catch (error) {
        setError('Failed to save calendar schedule');
      }
    };

  const generateHourOptions = () => {
    const options = [{ value: '', label: '--' }];
    for (let hour = 0; hour < 24; hour++) {
      options.push({ value: hour, label: `${hour}:00` });
    }
    return options;
  };

  const handleOpenImageDialog = (type) => {
    setImageDialogType(type);
    setSelectedImage('');
    setOpenImageDialog(true);
  };

  const handleImageSelect = (type, image) => {
    const imageBlob = userMediaImages[image];
    const imageObject = { name: image, blob: imageBlob };

    if (type === 'regular') {
        setSelectedRegularImage(imageObject);
    } else if (type === 'speeding') {
        setSelectedSpeedingImage(imageObject);
    }

    handleCloseImageDialog();
  };

  const calendarDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 }),
    end: endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 })
  });

  const getAllMonths = () => {
    const year = new Date().getFullYear();
    return Array.from({ length: 12 }, (_, i) => new Date(year, i, 1));
  };

  const getYearlyCalendar = () => {
    const yearStart = startOfWeek(new Date(new Date().getFullYear(), 0, 1), { weekStartsOn: 1 });
    const yearEnd = endOfWeek(new Date(new Date().getFullYear(), 11, 31), { weekStartsOn: 1 });
    const allDays = eachDayOfInterval({ start: yearStart, end: yearEnd });
    
    const weeks = [];
    let currentMonth = -1;
    
    while (allDays.length) {
      const week = allDays.splice(0, 7);
      const firstDay = week[0];
      
      // Add month label for the first week of each month
      if (firstDay.getMonth() !== currentMonth) {
        currentMonth = firstDay.getMonth();
        weeks.push({ isMonthLabel: true, month: currentMonth });
      }
      
      weeks.push({ isMonthLabel: false, days: week });
    }
    return weeks;
  };

  const hasValidTimeSlots = () => timeSlots.some(slot => slot.start !== null && slot.end !== null);


  const CustomDay = (props) => {
    const { day, selectedDays, ...other } = props;
    const isSelected = selectedDays.some(
      selected => 
        selected.month === day.getMonth() + 1 && 
        selected.day === day.getDate()
    );
    
    return (
      <PickersDay
        {...other}
        day={day}
        sx={{
          backgroundColor: isSelected ? `${primaryColor} !important` : secondaryColor,
          color: isSelected ? `${textColor} !important` : textColor,
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: isSelected ? 
              `${primaryColor}CC !important` : 
              `${secondaryColor}CC`
          }
        }}
      />
    );
  };
  
  return (
    <div style={{ 
      backgroundColor: primaryColor, 
      color: textColor
    }}>
      {loading ? (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <CircularProgress />
        </div>
      ) : !packageAllowance ? (
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: secondaryColor,
          color: textColor,
          padding: 20,
          textAlign: 'center',
          height: '100vh'
        }}>
          <div>
            <Typography variant="h4" gutterBottom>
              Calendar Schedule Access Restricted
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/help')}
              sx={{ 
                mt: 2,
                backgroundColor: primaryColor,
                color: textColor,
                '&:hover': {
                  backgroundColor: darkMode ? '#555' : '#e0e0e0',
                }
              }}
            >
              Contact Support
            </Button>
          </div>
        </div>
      ) : (
      <Grid container spacing={3}>
        
        {/* Left Column */}
        <Grid item xs={6}>
          <Paper style={{ 
            padding: '20px', 
            backgroundColor: secondaryColor,
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const allDays = eachDayOfInterval({
                  start: new Date(new Date().getFullYear(), 0, 1),
                  end: new Date(new Date().getFullYear(), 11, 31)
                });
                setSelectedDates(allDays.map(d => ({
                  month: d.getMonth() + 1,
                  day: d.getDate()
                })));
              }}
              sx={{ color: `${textColor} !important` }}
            >
              Select All
            </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedDates([...new Set([...selectedDates, ...getWorkdays()])])}
                sx={{ color: `${textColor} !important` }}
              >
                Select All Workdays
              </Button>
              <Button
                variant="outlined"
                onClick={() => setSelectedDates([])}
                sx={{ color: `${textColor} !important` }}
              >
                Unselect All
              </Button>
            </Box>



              {getAllMonths().map((monthDate, index) => {
              const calendarDays = eachDayOfInterval({
                start: startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 }),
                end: endOfWeek(endOfMonth(monthDate), { weekStartsOn: 1 })
              });

              return (
                <Paper key={index} sx={{ 
                  mb: 4, 
                  p: 2,
                  position: 'relative',
                  backgroundColor: secondaryColor
                }}>
                  <Typography variant="h6" sx={{

                    top: 48,
                    zIndex: 1,
                    backgroundColor: secondaryColor,
                    py: 1,
                    color: `${textColor} !important`,
                    fontWeight: 'bold'
                  }}>
                    {format(monthDate, 'MMMM yyyy')}
                  </Typography>

                  <Grid container columns={7}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <Grid item xs={1} key={day} sx={{ textAlign: 'center', py: 1 }}>
                        <Typography sx={{ color: `${textColor} !important` }}>{day}</Typography>
                      </Grid>
                    ))}
                    
                    {calendarDays.map((date, dayIndex) => {
                      const day = date.getDate();
                      const month = date.getMonth() + 1;
                      const isSelected = selectedDates.some(d => d.month === month && d.day === day);
                      const isCurrentMonth = isSameMonth(date, monthDate);

                      return (
                        <Grid item xs={1} key={dayIndex}>
                          <Button
                            fullWidth
                            disabled={!isCurrentMonth}
                            onClick={() => isCurrentMonth && handleDateSelect(date)}
                            sx={{
                              minWidth: 0,
                              height: 40,
                              backgroundColor: 'transparent',
                              color: isSelected ? 
                                '#4CAF50 !important' : 
                                (isCurrentMonth ? textColor : `${textColor}30`),
                              borderRadius: '4px',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              '&:hover': {
                                backgroundColor: isSelected ? 
                                  '#45a049 !important' : 
                                  (isCurrentMonth ? `${secondaryColor}CC` : 'transparent')
                              },
                              '&.Mui-disabled': {
                                color: `${textColor}30 !important`,
                                backgroundColor: 'transparent !important'
                              }
                            }}
                          >
                            {day}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              );
            })}
          </Paper>
        </Grid>

        {/* Right Column */}
        <Grid item xs={6}>
          <Paper style={{ padding: '20px', backgroundColor: secondaryColor }}>
            <TextField
              fullWidth
              label="Schedule Name"
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
              sx={{ mb: 3, '& .MuiInputBase-input': { color: `${textColor} !important` } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={changeImage}
                  onChange={(e) => setChangeImage(e.target.checked)}
                  sx={{ '& .MuiSvgIcon-root': { color: textColor } }}
                />
              }
              label="Change Display Images"
              sx={{ color: `${textColor} !important` }}
            />

            {changeImage && (
              <>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={6}>
                  <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <img 
                        src={selectedRegularImage.blob} 
                        alt="Please Select Regular Image" 
                        style={{ 
                          width: '128px', 
                          height: '96px', 
                          objectFit: 'contain',
                          border: `2px solid ${textColor}`,
                          marginBottom: '8px'
                        }}
                      />
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={() => handleOpenImageDialog('regular')}
                        sx={{ 
                          color: `${textColor} !important` 
                        }}
                      >
                        Select Regular Image
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                  <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%'
                    }}>
                      <img 
                        src={selectedSpeedingImage.blob} 
                        alt="Please Select Speeding Image" 
                        style={{ 
                          width: '128px', 
                          height: '96px', 
                          objectFit: 'contain',
                          border: `2px solid ${textColor}`
                        }}
                      />
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        onClick={() => handleOpenImageDialog('speeding')}
                        sx={{ mt: 1, color: `${textColor} !important` }}
                      >
                        Select Speeding Image
                      </Button>
                    </Box>
                    
                    
                  </Grid>
                </Grid>
              </>
            )}

            {[...Array(4)].map((_, index) => (
              <Grid container spacing={2} key={index} sx={{ mt: 2 }}>
                <Grid item xs={5}>
                  <Select
                    fullWidth
                    value={timeSlots[index]?.start ?? ''}
                    onChange={(e) => handleTimeChange(index, 'start', e.target.value)}
                    displayEmpty
                    sx={{ '& .MuiSelect-select': { color: `${textColor} !important` } }}
                  >
                    {generateHourOptions().map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={2} sx={{ textAlign: 'center' }}>
                  <Typography>to</Typography>
                </Grid>
                <Grid item xs={5}>
                  <Select
                    fullWidth
                    value={timeSlots[index]?.end ?? ''}
                    onChange={(e) => handleTimeChange(index, 'end', e.target.value)}
                    displayEmpty
                    sx={{ '& .MuiSelect-select': { color: `${textColor} !important` } }}
                  >
                    {generateHourOptions().map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
            ))}

            
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedSpeedLimit}
                  onChange={(e) => setSelectedSpeedLimit(e.target.checked)}
                  sx={{ '& .MuiSvgIcon-root': { color: textColor } }}
                />
              }
              label="Change Speed Limit in Time Slots"
              sx={{ color: `${textColor} !important` }}
            />
            {selectedSpeedLimit && (
              <>
                <TextField
                  fullWidth
                  type="number"
                  inputProps={{ 
                    min: 0,
                    step: 10 
                  }}
                  label="Speed Limit"
                  value={speedLimit || 0}
                  onChange={(e) => setSpeedLimit(Number(e.target.value))}
                  sx={{ mb: 3, '& .MuiInputBase-input': { color: `${textColor} !important` } }}
                />
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={flashEnabled}
                  onChange={(e) => setFlashEnabled(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-thumb': { color: textColor },
                    '& .Mui-checked': {
                      color: '#4CAF50 !important',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: flashEnabled ? '#4CAF50 !important' : undefined,
                    },
                  }}
                />
              }
              label={<span style={{ color: textColor }}>Flash</span>}
              sx={{ color: `${textColor} !important` }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={wigWagEnabled}
                  onChange={(e) => setWigWagEnabled(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-thumb': { color: textColor },
                    '& .Mui-checked': {
                      color: '#4CAF50 !important',
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: wigWagEnabled ? '#4CAF50 !important' : undefined,
                    },
                  }}
                />
              }
              label={<span style={{ color: textColor }}>Wig Wag</span>}
              sx={{ color: `${textColor} !important` }}
            />
            <Button 
              fullWidth 
              variant="contained" 
              onClick={handleSave}
              disabled={!scheduleName || !hasValidTimeSlots()}
              sx={{ 
                mt: 3, 
                backgroundColor: `#4CAF50 !important`, 
                color: textColor,
                '&.Mui-disabled': {
                  backgroundColor: `#4CAF5044 !important`
                }
              }}
            >
              Save Schedule
            </Button>

            {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
          </Paper>
        </Grid>
      </Grid>
      )}

      {/* Image Selection Dialog */}
      <Dialog 
        open={openImageDialog} 
        onClose={handleCloseImageDialog}
        PaperProps={{
          style: {
            backgroundColor: primaryColor,
            color: textColor,
          },
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: textColor }}>
          Select {imageDialogType === 'regular' ? 'Regular' : 'Speeding'} Image
        </DialogTitle>
        <DialogContent>
          {isLoadingImages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              {Object.entries(userMediaImages).map(([fileName, blobUrl]) => (
                <Grid item xs={12} sm={6} key={fileName}>
                  <Paper 
                    onClick={() => handleImageSelect(imageDialogType, fileName)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedImage === fileName ? 'green' : secondaryColor,
                      '&:hover': {
                        backgroundColor: selectedImage === fileName ? 'darkgreen' : 'rgba(255, 255, 255, 0.1)',
                      },
                      p: 2,
                    }}
                  >
                    <Grid container spacing={6} alignItems="center">
                      <Grid item xs={6}>
                        <Box
                          sx={{
                            width: '100%',
                            paddingTop: '75%',
                            position: 'relative',
                            backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <img
                              src={blobUrl}
                              alt={fileName}
                              style={{ 
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                aspectRatio: '64 / 48',
                              }}
                            />
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="subtitle1" sx={{ color: textColor, wordBreak: 'break-word' }}>
                          {fileName}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog} sx={{ color: textColor }}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarSchedules;





/*

create me a page for calendar schedule, keep the same style as the current device page for colors and textcolors, and replace everything else based on below: 

seperate to 2 columns, each column has half of the screen
left column:
textfield for calendar schedule name
calendar for user to select dates of this year, they should be kept in a list with month and day

right column:
a checkbox for change image, value should be bool
// below appear only if checkbox is checked

image preview section, similar to the display part of the old page of device
include 2 images side by side, and a button below each image to select the images

// end of appear only if checkbox is checked part

and below are 4 rows of time slot selector.
each row should have 2 dropdowns for start and end time
each dropdown should have 24 options as 24 hours, value sohuld be 0-23, and a option to unselect the time to null or empty
the 4 timeslots much not overlap with each other, and the end time must be greater than start time
and slot 2 must be greater than slot 1, and so on
value should be start1, end1, start2, end2, start3, end3, start4, end4
and below should be a button to save the schedule

// end of the page

when page opens, it should check if the user defined the calendar in the server
if yes, load the data and show it in the page
if no or error, show a empty to start with

* use !important for textcolors, to avoid inheriting from parent

*/